import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { OnboardingTypeEnum, useBoundStore } from '@Store/index';
import { AuthorizedUserOnboardingType } from '@Store/slices/authOnboarding/authOnboarding.types';
import { MAIN_INCOME_SOURCE } from '@Types/consts';
import type { Country } from '@Types/country';

export const cddRoutes = [
  RoutesMap.CDD_ACH_VOLUMES,
  RoutesMap.CDD_DOMESTIC_WIRE_VOLUMES,
  RoutesMap.CDD_INTERNATIONAL_WIRES_VOLUMES,
];

export const eddCommonRoutes = [
  RoutesMap.EDD_EMBEDDED_WHAT_DO_YOU_SELL,
  RoutesMap.EDD_EMBEDDED_TO_WHO_YOU_SELL,
  RoutesMap.EDD_EMBEDDED_WHY_DID_YOU_CHOOSE,
  RoutesMap.EDD_EMBEDDED_BUSINESS_COUNTRIES,
  RoutesMap.EDD_EMBEDDED_MONTHLY_PAYMENT_VOLUME,
  RoutesMap.EDD_WEBSITE_SOCIAL,
];

export const eddExpandedRoutes = [
  RoutesMap.EDD_INTERNATIONAL_ACTIVITY,
  RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS,
  RoutesMap.EDD_BUSINESS_SIZE,
  RoutesMap.EDD_ANNUAL_REVENUE,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.EDD_FUNDING_SOURCES,
  RoutesMap.EDD_BUSINESS_CAPITAL,
];

// PR Reviwer: what do we think of the new names?
export const eddAllRoutes = [...eddCommonRoutes, ...eddExpandedRoutes];

export const eddNonProfitRoutes = [
  RoutesMap.EDD_ORGANIZATION_ROLE,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_B,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_C,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_D,
  RoutesMap.EDD_NONPROFIT_DESCRIPTION_E,
  RoutesMap.EDD_BENEFICIARY_SELECTION,
  RoutesMap.EDD_NONPROFIT_FUNDING_SOURCES,
  RoutesMap.EDD_NONPROFIT_DONORS,
  RoutesMap.EDD_NONPROFIT_ORGANIZATION_SIZE,
  RoutesMap.EDD_NONPROFIT_MONTHLY_ACTIVITY,
  ...cddRoutes,
  RoutesMap.EDD_NONPROFIT_AFFILIATION,
  RoutesMap.EDD_NONPROFIT_SOCIAL_MEDIA,
];

export const eddCountryFlowMap: Record<string, RoutesMap[]> = {
  NON_US_EDD_AE: [
    RoutesMap.EDD_SANCTIONED_COUNTRIES,
    RoutesMap.EDD_CHARITIES,
    RoutesMap.EDD_MILITARY_PRECIOUS_METALS,
    RoutesMap.EDD_CASH_PAYMENTS,
  ],
  NON_US_EDD_PA: [
    RoutesMap.EDD_TAX_LEGAL_SERVICES,
    RoutesMap.EDD_FUND_TRANSFER,
    RoutesMap.EDD_INVESTMENT_CUSTODIAL,
  ],
};

export const allCountryEddRoutes: RoutesMap[] = [
  ...new Set(Object.values(eddCountryFlowMap).flat()),
];

export function getCountryEddRoutes(country?: Country | null): RoutesMap[] {
  if (!country?.eddName) return [];
  return eddCountryFlowMap[country.eddName] ?? [];
}

export function applyCountryEddToStack(stack: RoutesMap[], routesToAdd: RoutesMap[]): RoutesMap[] {
  // ponytail: filter + splice — no intermediate objects
  const withoutCountryEdd = stack.filter((r) => !allCountryEddRoutes.includes(r));
  if (routesToAdd.length === 0) return withoutCountryEdd;
  const planIndex = withoutCountryEdd.indexOf(RoutesMap.PLAN_SELECTION);
  if (planIndex === -1) return [...withoutCountryEdd, ...routesToAdd];
  const result = [...withoutCountryEdd];
  result.splice(planIndex, 0, ...routesToAdd);
  return result;
}

const defaultStack = [
  RoutesMap.WELCOME,
  RoutesMap.PERSONAL_DETAILS,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS,
];

const defaultStackNonUs = () => [
  RoutesMap.WELCOME,
  RoutesMap.PERSONAL_DETAILS,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...eddAllRoutes,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.NON_US_SOCURE,
];

export const recoveryStack = [
  RoutesMap.RESET_PASSWORD_REQUEST,
  RoutesMap.RECOVERY_TYPE,
  RoutesMap.RECOVERY_VERIFICATION_CODE,
  RoutesMap.RECOVERY_SET_PASSWORD,
  RoutesMap.RECOVERY_SUCCESS,
];

const accountantPortalOnboardingStack = [
  RoutesMap.ACCOUNTANT_PORTAL_WELCOME,
  RoutesMap.AUTH_USER_PHONE,
  RoutesMap.VERIFY_PHONE_NUMBER,
  RoutesMap.SET_PASSWORD,
  RoutesMap.ACCOUNTANT_PORTAL_COMPLETE,
];

const _getAuthorizedUserStack = (onboardingType: AuthorizedUserOnboardingType | undefined) => {
  switch (onboardingType) {
    case AuthorizedUserOnboardingType.AUTHORIZEDUSER_NOKYC:
      return [
        RoutesMap.INVITE_TEAM_MEMBER_INTRO,
        RoutesMap.AUTH_USER_PHONE,
        RoutesMap.VERIFY_PHONE_NUMBER,
        RoutesMap.SET_PASSWORD,
        RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
      ];
    case AuthorizedUserOnboardingType.AUTHORIZEDUSER_KYC:
      return [
        RoutesMap.INVITE_TEAM_MEMBER_INTRO,
        RoutesMap.AUTH_USER_PHONE,
        RoutesMap.VERIFY_PHONE_NUMBER,
        RoutesMap.SET_PASSWORD,
        RoutesMap.AUTH_USER_IDENTITY,
        RoutesMap.AUTH_USER_HOME_ADDRESS,
        RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
      ];
    case AuthorizedUserOnboardingType.KYC_ONLY:
      return [
        RoutesMap.INVITE_TEAM_MEMBER_INTRO,
        RoutesMap.VERIFY_PHONE_NUMBER,
        RoutesMap.AUTH_USER_IDENTITY,
        RoutesMap.AUTH_USER_HOME_ADDRESS,
        RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
      ];
    default:
      return defaultStack;
  }
};

const _getPartnerEmbeddedWhiteGlove = (isUsFlow: boolean) => {
  const mainIncomeSource = useBoundStore.getState().getCustomerData().customerData
    .customerBusinessData.mainIncomeSource;

  const isNonProfit = mainIncomeSource === MAIN_INCOME_SOURCE.NON_PROFIT;

  let eddBlock: RoutesMap[];
  if (isNonProfit) {
    eddBlock = eddNonProfitRoutes;
  } else if (!isUsFlow) {
    eddBlock = eddAllRoutes;
  } else {
    eddBlock = cddRoutes;
  }

  return [
    RoutesMap.WELCOME,
    ...eddBlock,
    RoutesMap.EDD_INTENDED_USE,
    RoutesMap.PLAN_SELECTION,
    RoutesMap.REVIEW,
    RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
  ];
};

const _getPartnerEmbeddedStack = () => [
  RoutesMap.WELCOME,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
];

const _getPartnerEmbeddedNonUsStack = () => [
  RoutesMap.WELCOME,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...eddAllRoutes,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.NON_US_SOCURE,
];

const _getPartnerConnect = () => [
  RoutesMap.WELCOME,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS,
];

const _getPartnerConnectNonUs = () => [
  RoutesMap.WELCOME,
  RoutesMap.PHONE,
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...eddAllRoutes,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.NON_US_SOCURE,
];

const _getAccountantAsOwnerStack = () => [
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS,
];

const _getAccountantAsOwnerNonUsStack = () => [
  RoutesMap.IDENTITY,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...eddAllRoutes,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.NON_US_SOCURE,
];

const _getDirectMultiBusiness = () => [
  RoutesMap.MULTI_BUSINESS_ONBOARDING_INTRO,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...cddRoutes,
  RoutesMap.EDD_INTENDED_USE,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
];
const _getDirectNonUsMultiBusiness = () => [
  RoutesMap.MULTI_BUSINESS_ONBOARDING_INTRO,
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.REGISTERED_ADDRESS,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.MAILING_ADDRESS,
  RoutesMap.OWNER_CENTER,
  ...eddAllRoutes,
  RoutesMap.PLAN_SELECTION,
  RoutesMap.REVIEW,
  RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
];
const getUboOnboardingStack = () => [
  RoutesMap.UBO_ONBOARDING_INTRO,
  RoutesMap.UBO_ONBOARDING_VALIDATE_PHONE,
  RoutesMap.UBO_ONBOARDING_ID_VERIFICATION,
  RoutesMap.UBO_ONBOARDING_CREATE_PASSWORD,
  RoutesMap.SUCCESS,
];
const getUboOnboardingStackNonUs = () => [
  RoutesMap.UBO_ONBOARDING_INTRO,
  RoutesMap.UBO_ONBOARDING_VALIDATE_PHONE,
  RoutesMap.UBO_ONBOARDING_CREATE_PASSWORD,
  RoutesMap.UBO_ONBOARDING_DOCV,
  RoutesMap.SUCCESS,
];
const _removeSpecificRoute = (route: RoutesMap, stack: RoutesMap[]) => {
  return stack.filter((item) => item !== route);
};

const _handleRoutesCleanup = (stack: RoutesMap[]) => {
  let stackToModify = [...stack];
  const onboardingTypeEnum = useBoundStore.getState().getCustomerData()
    .customerData.onboardingTypeEnum;

  if (onboardingTypeEnum === OnboardingTypeEnum.MULTI_BUSINESS) {
    [
      RoutesMap.PERSONAL_DETAILS,
      RoutesMap.PHONE,
      RoutesMap.IDENTITY,
      RoutesMap.HOME_ADDRESS,
    ].forEach((route) => {
      stackToModify = _removeSpecificRoute(route, stackToModify);
    });
  }

  return stackToModify;
};

export const getNavigationStackMap = (): RoutesMap[] => {
  const { onboardingFlowIdentifier } = useBoundStore.getState().onboardingSlice.getOnboardingData();
  const onboardingType = useBoundStore.getState().getOnboardingType();
  const isUboFlow = onboardingFlowIdentifier === OnboardingFlowIdentifier.UBO;
  const isUsFlow = isUboFlow
    ? useBoundStore.getState().isUsFlow
    : useBoundStore.getState().getIsUsFlow();
  const isUboValidForNonUsFlow = useBoundStore.getState().getIsUboValidForNonUsFlow();
  const isUsNavigationStack = isUsFlow && !isUboValidForNonUsFlow;
  const rawStack = getNavStackSwitch(onboardingFlowIdentifier, onboardingType, isUsNavigationStack);
  return _handleRoutesCleanup(rawStack);
};

const getNavStackSwitch = (
  onboardingFlowIdentifier: OnboardingFlowIdentifier,
  onboardingType: AuthorizedUserOnboardingType | undefined,
  isUsFlow: boolean
) => {
  const isWhiteGlove = useBoundStore.getState().onboardingSlice.getIsWhiteGloveFlow();
  if (isWhiteGlove) {
    return _getPartnerEmbeddedWhiteGlove(isUsFlow);
  }

  switch (onboardingFlowIdentifier) {
    case OnboardingFlowIdentifier.DIRECT:
      return isUsFlow ? defaultStack : defaultStackNonUs();

    case OnboardingFlowIdentifier.PARTNER_EMBEDDED:
      return isUsFlow ? _getPartnerEmbeddedStack() : _getPartnerEmbeddedNonUsStack();

    case OnboardingFlowIdentifier.PARTNER_CONNECT:
      return isUsFlow ? _getPartnerConnect() : _getPartnerConnectNonUs();

    case OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS:
      return isUsFlow ? _getDirectMultiBusiness() : _getDirectNonUsMultiBusiness();

    case OnboardingFlowIdentifier.UBO:
      return isUsFlow ? getUboOnboardingStack() : getUboOnboardingStackNonUs();

    case OnboardingFlowIdentifier.AUTHORIZED_USER:
      return _getAuthorizedUserStack(onboardingType);

    case OnboardingFlowIdentifier.AUTHORIZED_USER_ACCOUNTANT_PORTAL:
      return accountantPortalOnboardingStack;

    case OnboardingFlowIdentifier.ACCOUNTANT_AS_OWNER:
      return isUsFlow ? _getAccountantAsOwnerStack() : _getAccountantAsOwnerNonUsStack();

    default:
      return defaultStack;
  }
};
